<template>
	<div class="cm-thread">
		<div class="cm-head">
			<v-icon small class="mr-1">{{ orderId ? 'mdi-package-variant-closed' : 'mdi-account-outline' }}</v-icon>
			<span class="cm-head-title">{{ orderId ? 'Conversazione ordine ' + orderId : 'Conversazione con il cliente' }}</span>
			<span v-if="messaggi.length" class="cm-head-count">· {{ messaggi.length }} {{ messaggi.length === 1 ? 'messaggio' : 'messaggi' }}</span>
			<v-spacer></v-spacer>
			<v-btn v-if="orderMongoId" x-small outlined color="primary" @click="$emit('open-order')"><v-icon left x-small>mdi-magnify</v-icon>Apri ordine</v-btn>
		</div>

		<div ref="scroll" class="cm-body">
			<div v-if="caricamento" class="cm-state"><v-progress-circular indeterminate size="22" width="2" color="primary"></v-progress-circular></div>
			<div v-else-if="errore" class="cm-state error--text">Conversazione non disponibile</div>
			<template v-else>
				<div v-if="troncata" class="cm-note">Sono mostrati solo gli ultimi {{ messaggi.length }} messaggi</div>
				<div
					v-for="(m, i) in messaggi"
					:key="m._id || 'n' + i"
					:ref="'m' + m._id"
					class="cm-row"
					:class="[m.owner === 'Store' ? 'cm-row--store' : 'cm-row--customer', { 'cm-row--focus': m._id === messageId }]"
				>
					<div class="cm-avatar" :class="m.owner === 'Store' ? 'cm-avatar--store' : 'cm-avatar--customer'">
						<v-icon small :color="m.owner === 'Store' ? 'white' : 'primary'">{{ m.owner === 'Store' ? 'mdi-storefront-outline' : 'mdi-account' }}</v-icon>
					</div>
					<div class="cm-bubble">
						<div class="cm-meta">
							<span class="cm-sender">{{ mittente(m) }}</span>
							<span class="cm-date">{{ m.created_at | moment('DD/MM/YYYY HH:mm') }}</span>
						</div>
						<div v-if="mostraOggetto(i)" class="cm-subject">{{ oggetto(m) }}</div>
						<div v-if="corpi[m._id].corpo" class="cm-text" v-html="corpi[m._id].corpo"></div>
						<div v-else-if="!corpi[m._id].citato" class="cm-empty">Nessun testo</div>
						<template v-if="corpi[m._id].citato">
							<button type="button" class="cm-quote-toggle" @click="toggle(m._id)">
								<v-icon x-small>{{ aperti[m._id] ? 'mdi-chevron-up' : 'mdi-dots-horizontal' }}</v-icon>
								{{ aperti[m._id] ? 'Nascondi testo citato' : 'Mostra testo citato' }}
							</button>
							<div v-if="aperti[m._id]" class="cm-text cm-quoted" v-html="corpi[m._id].citato"></div>
						</template>
						<div v-if="(m.attachments || []).length" class="cm-attachs">
							<template v-for="(a, ai) in m.attachments">
								<a v-if="isImage(a)" :key="'i' + ai" :href="a" target="_blank" rel="noopener noreferrer" class="cm-thumb" :title="nomeFile(a)">
									<img :src="a" alt="" loading="lazy" />
								</a>
								<v-chip v-else :key="'f' + ai" small outlined :href="a" target="_blank" rel="noopener noreferrer" class="cm-file">
									<v-icon left small>{{ iconaFile(a) }}</v-icon>
									<span class="text-truncate">{{ nomeFile(a) }}</span>
								</v-chip>
							</template>
						</div>
					</div>
				</div>
				<div v-if="!messaggi.length" class="cm-state">Nessun messaggio</div>
			</template>
		</div>
	</div>
</template>

<script>
import { pulisciEmail, testoDaEntita } from '@/utils/emailHtml';

const IMMAGINI = ['png', 'gif', 'jpeg', 'jpg', 'webp', 'svg'];

// "R: Fwd: Oggetto" e "Oggetto" sono lo stesso argomento
const oggettoBase = s =>
	String(s || '')
		.replace(/^(\s*(r|re|rif|fw|fwd|i|inoltra)\s*:\s*)+/i, '')
		.trim()
		.toLowerCase();

// conversazione email di un ordine (o di un cliente): cliente a sinistra, negozio a destra
export default {
	name: 'CommunicationThread',
	props: {
		// messaggio da cui si apre la conversazione: viene evidenziato e portato in vista
		messageId: { type: String, default: '' },
		// messaggi gia' caricati (es. scheda ordine): se presenti non si scarica niente e la chat
		// si apre sull'ultimo messaggio; quando ne arriva uno nuovo scorre in fondo
		messages: { type: Array, default: null },
		orderId: { type: String, default: '' },
		orderMongoId: { type: String, default: '' },
	},
	data() {
		return { messaggi: [], troncata: false, caricamento: true, errore: false, aperti: {} };
	},
	computed: {
		// HTML ripulito una volta sola per messaggio
		corpi() {
			const out = {};
			this.messaggi.forEach(m => {
				out[m._id] = pulisciEmail(m.text);
			});
			return out;
		},
	},
	watch: {
		messages(lista) {
			if (!lista) return;
			this.messaggi = this.ordinati(lista);
			this.$nextTick(this.portaInVista);
		},
	},
	async mounted() {
		if (this.messages) {
			this.messaggi = this.ordinati(this.messages);
			this.caricamento = false;
			this.$nextTick(this.portaInVista);
			return;
		}
		const res = await this.$store.dispatch('communications/fetchThread', this.messageId);
		this.caricamento = false;
		if (res.error) {
			this.errore = true;
			return;
		}
		this.messaggi = res.data;
		this.troncata = res.truncated;
		this.$emit('caricato', this.messaggi);
		this.$nextTick(this.portaInVista);
	},
	methods: {
		// risposta appena inviata (es. da /communications): si accoda e si scorre in fondo
		aggiungi(msg) {
			if (!msg || !msg._id) return;
			this.messaggi = this.ordinati([...this.messaggi, msg]);
			this.$nextTick(() => {
				const box = this.$refs.scroll;
				if (box) box.scrollTop = box.scrollHeight;
			});
		},
		ordinati(lista) {
			return lista.filter(m => m && m._id).slice().sort((a, b) => new Date(a.created_at) - new Date(b.created_at));
		},
		portaInVista() {
			const box = this.$refs.scroll;
			const el = (this.$refs['m' + this.messageId] || [])[0];
			if (!box) return;
			// .cm-body e' position: relative, quindi offsetTop e' gia' relativo al contenitore
			box.scrollTop = el ? el.offsetTop - 12 : box.scrollHeight;
		},
		toggle(id) {
			this.$set(this.aperti, id, !this.aperti[id]);
		},
		mittente(m) {
			if (m.owner === 'Store') return (m.user && m.user.name) || 'Negozio';
			const b = m.buyer || {};
			return testoDaEntita([b.name, b.lastname].filter(Boolean).join(' ')) || b.email || 'Cliente';
		},
		oggetto(m) {
			return testoDaEntita(m.subject);
		},
		// l'oggetto si ripete solo quando cambia argomento
		mostraOggetto(i) {
			const m = this.messaggi[i];
			if (!m.subject) return false;
			return i === 0 || oggettoBase(m.subject) !== oggettoBase(this.messaggi[i - 1].subject);
		},
		nomeFile(url) {
			return decodeURIComponent(String(url).split('/').pop().split('?')[0]);
		},
		isImage(url) {
			return IMMAGINI.includes(this.nomeFile(url).split('.').pop().toLowerCase());
		},
		iconaFile(url) {
			const ext = this.nomeFile(url).split('.').pop().toLowerCase();
			if (ext === 'pdf') return 'mdi-file-pdf-box';
			if (['doc', 'docx', 'odt'].includes(ext)) return 'mdi-file-word-outline';
			if (['xls', 'xlsx', 'csv', 'ods'].includes(ext)) return 'mdi-file-excel-outline';
			return 'mdi-paperclip';
		},
	},
};
</script>

<style scoped>
.cm-thread {
	border: 1px solid rgba(0, 0, 0, 0.12);
	border-radius: 10px;
	overflow: hidden;
	background: #f3f6fa;
	max-width: 900px;
	margin: 0 auto;
}
.cm-head {
	display: flex;
	align-items: center;
	gap: 4px;
	padding: 8px 12px;
	background: #fff;
	border-bottom: 1px solid #e1e7ef;
	font-size: 13px;
}
.cm-head-title {
	font-weight: 600;
	color: #1f2d3d;
}
.cm-head-count {
	color: #5f6b7a;
}
.cm-body {
	position: relative;
	max-height: 560px;
	overflow-y: auto;
	padding: 12px;
	-webkit-overflow-scrolling: touch;
}
.cm-state {
	text-align: center;
	color: #5f6b7a;
	font-size: 13px;
	padding: 24px 0;
}
.cm-note {
	text-align: center;
	font-size: 12px;
	color: #5f6b7a;
	margin-bottom: 10px;
}
.cm-row {
	display: flex;
	align-items: flex-start;
	gap: 8px;
	margin-bottom: 12px;
}
.cm-row--store {
	flex-direction: row-reverse;
}
.cm-avatar {
	flex: 0 0 30px;
	width: 30px;
	height: 30px;
	border-radius: 50%;
	display: flex;
	align-items: center;
	justify-content: center;
}
.cm-avatar--store {
	background: #01579b;
}
.cm-avatar--customer {
	background: #fff;
	border: 1px solid #d5dee9;
}
.cm-bubble {
	min-width: 0;
	max-width: 82%;
	padding: 8px 12px;
	border-radius: 12px;
	font-size: 14px;
	line-height: 1.5;
	color: #1f2d3d;
	word-break: break-word;
	box-shadow: 0 1px 1px rgba(16, 38, 66, 0.06);
}
.cm-row--customer .cm-bubble {
	background: #fff;
	border: 1px solid #e1e7ef;
	border-top-left-radius: 3px;
}
.cm-row--store .cm-bubble {
	background: #e6eff8;
	border: 1px solid #c9dbee;
	border-top-right-radius: 3px;
}
.cm-row--focus .cm-bubble {
	box-shadow: 0 0 0 2px #01579b;
}
.cm-meta {
	display: flex;
	flex-wrap: wrap;
	align-items: baseline;
	gap: 2px 8px;
	font-size: 12px;
	margin-bottom: 2px;
}
.cm-sender {
	font-weight: 600;
	color: #01579b;
}
.cm-date {
	color: #5f6b7a;
}
.cm-subject {
	font-weight: 600;
	margin-bottom: 4px;
}
.cm-empty {
	color: #5f6b7a;
	font-style: italic;
}
/* HTML delle email (gia' ripulito): paragrafi compatti, tabelle e testi lunghi contenuti */
.cm-text ::v-deep p {
	margin: 0 0 4px;
}
.cm-text ::v-deep table {
	max-width: 100%;
	display: block;
	overflow-x: auto;
	border-collapse: collapse;
}
.cm-text ::v-deep td,
.cm-text ::v-deep th {
	padding: 2px 6px;
	vertical-align: top;
}
.cm-text ::v-deep pre {
	white-space: pre-wrap;
}
.cm-text ::v-deep blockquote {
	margin: 4px 0;
	padding-left: 8px;
	border-left: 3px solid #c9d3df;
	color: #5f6b7a;
}
.cm-text ::v-deep a {
	word-break: break-all;
}
.cm-quote-toggle {
	display: inline-flex;
	align-items: center;
	gap: 4px;
	margin-top: 4px;
	font-size: 12px;
	color: #01579b;
	background: rgba(1, 87, 155, 0.08);
	border-radius: 10px;
	padding: 1px 8px;
}
.cm-quoted {
	margin-top: 6px;
	padding-left: 8px;
	border-left: 3px solid #c9d3df;
	color: #5f6b7a;
	font-size: 13px;
}
.cm-attachs {
	display: flex;
	flex-wrap: wrap;
	gap: 6px;
	margin-top: 8px;
}
.cm-thumb img {
	display: block;
	width: 64px;
	height: 64px;
	object-fit: cover;
	border-radius: 6px;
	border: 1px solid #d5dee9;
	background: #fff;
}
.cm-file {
	max-width: 220px;
}

@media (max-width: 768px) {
	.cm-thread {
		margin: 0 -4px;
	}
	.cm-body {
		max-height: 65vh;
		padding: 8px;
	}
	.cm-bubble {
		max-width: calc(100% - 38px);
	}
	.cm-head-count {
		display: none;
	}
}
</style>
